import { SdkContext } from '../internal'

/**
 * F-91 phase 2: posts, articles and short posts from a spreadsheet.
 * Owner and admin of the `X-CommunityId`; no route carries the community.
 */

export type ContentImportTarget = 'ignore' | 'title' | 'body' | 'authorEmail' | 'publishedAt' | 'space' | 'type' | 'tags'

export type ContentImportType = 'post' | 'article' | 'short'

export type ContentImportStatus = 'ANALYZED' | 'QUEUED' | 'IMPORTING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'ABANDONED'

export interface ContentImportColumn {
  index: number
  name: string
  header?: string
  samples: string[]
  suggestion?: { target: ContentImportTarget; confidence: 'high' | 'low' } | null
  distinctValues?: string[]
}

export interface ContentImportMappingEntry {
  column: number
  target: ContentImportTarget
}

export interface ContentImportOptions {
  spaceId?: string
  type?: ContentImportType
  visibility?: 'public' | 'members'
  dateOrder?: 'DMY' | 'MDY'
  /** A row whose author e-mail is nobody in the community: skipped, or published as the importer. */
  unknownAuthor?: 'skip' | 'importer'
}

export interface ContentImportSummary {
  totalRows: number
  toImport: number
  byType?: Partial<Record<ContentImportType, number>>
  backdated: number
  withoutDate: number
  skipped: number
  skippedByReason?: Record<string, number>
  withWarnings: number
  warningsByCode?: Record<string, number>
  imagesToFetch: number
  /** Bytes of the bodies to import, as HTML; the start is refused above `caps.bodyBytes`. */
  bodyBytes?: number
  caps?: { bodyBytes: number; imagesPerRow: number; imagesPerImport: number; imageBytesPerImport: number }
  dateOrder?: 'DMY' | 'MDY' | null
  timeZone?: string | null
}

export interface ContentImportWarning {
  code: string
  value?: string
  url?: string
  reason?: string
  max?: number
  maxImages?: number
  maxMb?: number
}

/** A row of the preview, as it will be saved, or skipped. */
export interface ContentImportPreviewRow {
  rowNumber: number
  skipped: boolean
  reason?: string
  type?: ContentImportType
  title?: string | null
  bodyPreview?: string | null
  space?: { id: string; title: string } | null
  author?: { id: string; name: string | null; email: string | null; isImporter: boolean } | null
  publishedAt?: string | null
  backdated?: boolean
  tags?: string[]
  images?: number
  warnings?: ContentImportWarning[]
}

export interface ContentImportAnalysis {
  id: string
  fileName: string
  status?: ContentImportStatus
  fileType?: 'csv' | 'xlsx'
  sheets?: { name: string; rows: number }[]
  sheet?: string | null
  encoding?: string | null
  delimiter?: string | null
  headerRow: number
  headerDetected?: boolean
  rawRows?: string[][]
  columns: ContentImportColumn[]
  totalRows: number
  summary?: ContentImportSummary | null
  rows?: ContentImportPreviewRow[] | null
}

/** A row of the report: skipped, failed, or imported with warnings. */
export interface ContentImportRow {
  rowNumber: number
  result: 'IMPORTED' | 'SKIPPED' | 'FAILED'
  itemType?: 'content' | 'status' | null
  itemId?: string | null
  title?: string | null
  reason?: string | null
  warnings?: ContentImportWarning[] | null
}

export interface ContentImport {
  id: string
  source?: string
  status: ContentImportStatus
  fileName: string
  totalRows: number
  processedRows: number
  importedCount: number
  skippedCount: number
  failedCount: number
  warningCount: number
  createdAt: string
  startedAt?: string | null
  completedAt?: string | null
  createdBy?: { id: string; name: string | null } | null
  rows?: ContentImportRow[]
}

export interface ContentImportReadBody {
  sheet?: string
  headerRow?: number
  delimiter?: string
  encoding?: string
}

export class ContentImportsResource {
  constructor(private ctx: SdkContext) {}

  /** Uploads the file and answers the analysis. Nothing is imported. */
  async analyze(file: File): Promise<ContentImportAnalysis> {
    const fd = new FormData()
    fd.append('file', file)
    return this.ctx.call({ httpMethod: 'POST', path: '/import/spreadsheet', hasBody: true }, { body: fd })
  }

  /** Re-reads the file; with a mapping and options, also the summary and the first 10 rows. Writes nothing. */
  async preview(
    id: string,
    body: ContentImportReadBody & { mapping?: ContentImportMappingEntry[]; options?: ContentImportOptions }
  ): Promise<ContentImportAnalysis> {
    return this.ctx.call({ httpMethod: 'POST', path: '/import/spreadsheet/{id}/preview', hasBody: true }, { id, body })
  }

  /** Confirms and queues (202). */
  async start(
    id: string,
    body: ContentImportReadBody & { mapping: ContentImportMappingEntry[]; options: ContentImportOptions }
  ): Promise<ContentImport> {
    return this.ctx.call({ httpMethod: 'POST', path: '/import/spreadsheet/{id}/start', hasBody: true }, { id, body })
  }

  async get(id: string): Promise<ContentImport> {
    return this.ctx.call({ httpMethod: 'GET', path: '/import/spreadsheet/{id}' }, { id })
  }

  /** The community's spreadsheet imports, newest first. */
  async list(): Promise<ContentImport[]> {
    const result = await this.ctx.call<unknown>({ httpMethod: 'GET', path: '/import/spreadsheet' }, { query: { limit: 50 } })
    if (Array.isArray(result)) return result as ContentImport[]
    const r = result as { items?: ContentImport[] } | null
    return r?.items ?? []
  }

  /** Cancels a queued or running import; what already went in stays. */
  async cancel(id: string): Promise<void> {
    await this.ctx.call({ httpMethod: 'DELETE', path: '/import/{importId}' }, { importId: id })
  }
}
